/**
 * @summary - Component that Provides Work Group Creation Page
 * @author Yuvasri S
 * @since 2024-05-28
 */
import React, { useState, useEffect } from "react";
import { Box, Typography, IconButton, Grid } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { BreadcrumbBar } from "../../assets/styled-components/breadcrumbBar";
import CustomTextField from "../commoncomponents/CustomTextField";
import { DefaultCard } from "../../assets/styled-components/card";
import CustomFooter from "../commoncomponents/CustomFooter";
import { useNavigate, useLocation } from "react-router";
import CustomSelectInput from "../commoncomponents/CustomSelectInput";
import axios from "axios";
import {
  getAuthToken,
  getcompanyId,
  getFacilityId,
  getCreatedBy,
  getModifiedBy,
} from "../../utility/tokenUtility";
import { FemsGraph, BemsGraph, MasterGraph } from "../../utility/apiURLS";
import CustomDialog from "../commoncomponents/CustomDialog";
import HomeIconButton from "../commoncomponents/HomeIconButton";
import PageTitle from "../commoncomponents/PageTitle";

const CreateWorkGroup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [service, setService] = useState("");
  const [serviceOptions, setServiceOptions] = useState([]);
  const [wgcode, setWgCode] = useState("");
  const [wgdesc, setWgDesc] = useState("");
  const [workGroupId, setWorkGroupId] = useState("");

  const [readOnly, setReadOnly] = useState(false);
  const [readOnlyEdit, setReadOnlyEdit] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isView, setIsView] = useState(false);

  //cancel validation
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  //Addnew validation
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [unsavedAddDialogOpen, setUnsavedAddDialogOpen] = useState(false);

  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errors, setErrors] = useState({
    service: false,
    wgcode: false,
    wgdesc: false,
  });

  const [errorMessages, setErrorMessages] = useState({
    service: "",
    wgcode: "",
    wgdesc: "",
  });

  const token = getAuthToken("id_token");
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const companyId = getcompanyId("companyId");
  const facilityId = getFacilityId("facilityId");
  const createdBy = getCreatedBy("createdBy");
  const modifiedBy = getModifiedBy("modifiedBy");

  useEffect(() => {
    const fetchServiceOptions = async () => {
      try {
        const serviceDetailsResponse = await axios.get(
          `${MasterGraph}/api/masservicedetails`,
          config
        );

        const uniqueServices = serviceDetailsResponse.data.map((service) => ({
          value: service.service,
          label: service.service,
          id: service.id,
        }));

        setServiceOptions(uniqueServices);

        // Check if there is state passed from the navigation
        if (location.state) {
          //   const { servicename, assetcategory, assetcategorydesc, id, readOnly, readOnlyEdit, mode } = location.state;
          const {
            wgcode,
            wgdesc,
            serviceid,
            wgId,
            serviceName,
            readOnly,
            readOnlyEdit,
            mode,
          } = location.state;


          const selectedService = uniqueServices.find(
            (option) => option.label === serviceName
          );
          setService(selectedService);
          setWgCode(wgcode);
          setWgDesc(wgdesc);
          setWorkGroupId(wgId);
          setReadOnly(!!readOnly);
          setReadOnlyEdit(!!readOnlyEdit);
          if (mode === "edit") {
            setIsEdit(true);
            setIsView(false);
          } else if (mode === "view") {
            setIsView(true);
            setIsEdit(false);
          } else {
            setIsEdit(false);
            setIsView(false);
          }
        }
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };

    fetchServiceOptions();
  }, [location.state]);

  const handleSubmit = (e) => {
    e.preventDefault();

    let formIsValid = true;
    const newErrors = { service: false, wgcode: false, wgdesc: false };
    const newErrorMessages = { service: "", wgcode: "", wgdesc: "" };

    if (!service) {
      formIsValid = false;
      newErrors.service = true;
      newErrorMessages.service = "Service is required";
    }
    if (!wgcode) {
      formIsValid = false;
      newErrors.wgcode = true;
      newErrorMessages.wgcode = "Work Group Code is required";
    } else if (wgcode.startsWith(" ")) {
      formIsValid = false;
      newErrors.wgcode = true;
      newErrorMessages.wgcode = "Space not allowed at the beginning";
    } else if (wgcode.includes("  ")) {
      formIsValid = false;
      newErrors.wgcode = true;
      newErrorMessages.wgcode = "Double Space not allowed";
    } else if (!/^[a-zA-Z0-9,!@#$%&*()_\-\/ ]*$/.test(wgcode)) {
      formIsValid = false;
      newErrors.wgcode = true;
      newErrorMessages.wgcode = "Invalid Characters in Work Group Code";
    }
    if (!wgdesc) {
      formIsValid = false;
      newErrors.wgdesc = true;
      newErrorMessages.wgdesc = "Work Group Description is required";
    } else if (wgdesc.startsWith(" ")) {
      formIsValid = false;
      newErrors.wgdesc = true;
      newErrorMessages.wgdesc = "Space not allowed at the beginning";
    } else if (wgdesc.includes("  ")) {
      formIsValid = false;
      newErrors.wgdesc = true;
      newErrorMessages.wgdesc = "Double Space not allowed";
    } else if (!/^[a-zA-Z0-9,!@#$%&*()_\- .]*$/.test(wgdesc)) {
      // Updated regex to allow the period (.)
      formIsValid = false;
      newErrors.wgdesc = true;
      newErrorMessages.wgdesc = "Invalid Characters in Work Group Description";
    }

    setErrors(newErrors);
    setErrorMessages(newErrorMessages);

    if (formIsValid) {
      handleSave();
      setHasUnsavedChanges(false);
    }
  };

  const handleCancel = () => {
    if (isView) {
      navigate(-1, { replace: true });
    } else {
      if (hasUnsavedChanges) {
        setCancelDialogOpen(true);
      } else {
        navigate(-1, { replace: true });
      }
    }
  };


  const handleSave = async () => {
    try {
      let endpoint = "";
      let method = "post";

      if (service.value === "FEMS") {
        endpoint = `${FemsGraph}/api/femworkgroupdetails`;
        if (isEdit) {
          endpoint = `${FemsGraph}/api/femworkgroupdetails/${workGroupId}`;
          method = "put";
        }
      } else if (service.value === "BEMS") {
        endpoint = `${BemsGraph}/api/bemworkgroupdetails`;
        if (isEdit) {
          endpoint = `${BemsGraph}/api/bemworkgroupdetails/${workGroupId}`;
          method = "put";
        }
      } else {
        console.error("Unsupported service selected");
        return;
      }

      const postData = {
        serviceid: service.id,
        wgcode: wgcode,
        wgdesc: wgdesc,
        id: workGroupId,
        facilityid: facilityId,
        createdby: createdBy,
        ...(isEdit ? { timestamp: location.state.timestamp } : {})
      };
      const response = await axios({
        method: method,
        url: endpoint,
        data: postData,
        headers: config.headers,
      });
      setIsSubmitted(true);
      setDialogOpen(true);
    } catch (error) {
      console.error("Error saving data:", error);
      if (error.response && error.response.status === 400) {
        setErrorMessage("Work Group Code Already Exists");
      } else {
        setErrorMessage(
          "An unexpected error occurred. Please try again later."
        );
      }
      setErrorDialogOpen(true);
    }
  };

  const handleServiceChange = (newValue) => {
    setHasUnsavedChanges(true);
    setIsSubmitted(false);
    setService(newValue);
    if (newValue) {
      setErrors((prevErrors) => ({ ...prevErrors, service: false }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, service: "" }));
    }
  };

  const handleWgCodeChange = (newValue) => {
    setHasUnsavedChanges(true);
    setIsSubmitted(false);
    setWgCode(newValue);
    const regex = /^[a-zA-Z0-9 /-]*$/;
    if (!regex.test(newValue)) {
      setErrors((prevErrors) => ({ ...prevErrors, wgcode: true }));
      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        wgcode: "Invalid Characters in Work Group Code",
      }));
    } else if (newValue.startsWith(" ")) {
      setErrors((prevErrors) => ({ ...prevErrors, wgcode: true }));
      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        wgcode: "Space not allowed at the beginning",
      }));
    } else if (newValue.includes("  ")) {
      setErrors((prevErrors) => ({ ...prevErrors, wgcode: true }));
      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        wgcode: "Double Space not allowed",
      }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, wgcode: false }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, wgcode: "" }));
    }
  };

  const handleWgDescChange = (newValue) => {
    setHasUnsavedChanges(true);
    setIsSubmitted(false);
    setWgDesc(newValue);
    const regex = /^[a-zA-Z0-9,!@#$%&*()_\- .]*$/;

    if (!regex.test(newValue)) {
      setErrors((prevErrors) => ({ ...prevErrors, wgdesc: true }));
      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        wgdesc: "Invalid Characters in Work Group Description",
      }));
    } else if (newValue.startsWith(" ")) {
      setErrors((prevErrors) => ({ ...prevErrors, wgdesc: true }));
      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        wgdesc: "Space not allowed at the beginning",
      }));
    } else if (newValue.includes("  ")) {
      setErrors((prevErrors) => ({ ...prevErrors, wgdesc: true }));
      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        wgdesc: "Double Space not allowed",
      }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, wgdesc: false }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, wgdesc: "" }));
    }
  };


  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
  };
  const handleCancelDialogConfirm = () => {
    setCancelDialogOpen(false);
    navigate(-1, { replace: true });
  };
  const handleAdd = () => {
    if (!isSubmitted && (service || wgcode || wgdesc)) {
      setUnsavedAddDialogOpen(true);
    } else {
      clearForm();
    }
  };


  const clearForm = () => {
    setService("");
    setWgCode("");
    setWgDesc("");
    setIsEdit(false);
    setIsView(false);
    setWorkGroupId("");
    setErrors({ service: false, wgcode: false, wgdesc: false });
    setErrorMessages({ service: "", wgcode: "", wgdesc: "" });
  };
  const handleUnsavedAddDialogClose = () => {
    setUnsavedAddDialogOpen(false);
  };

  const handleUnsavedAddConfirm = () => {
    clearForm();
    setUnsavedAddDialogOpen(false);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    // if (isEdit) {
    //     navigate(-1, { replace: true });
    // }
  };


  return (
    <>
      <PageTitle
        isEdit={isEdit}
        isView={isView}
        editTitle={"Edit Work Group"}
        viewTitle={"View Work Group"}
        title={"Create Work Group"}
      />
      <Box mt={{ xs: 1, md: 2 }}>
        <DefaultCard sx={{ height: "100%" }}>
          <form onSubmit={handleSubmit} style={{ position: "relative" }}>
            <Grid container gap={3}>
              <Grid item xs={6} md={3.8}>
                <CustomSelectInput
                  id="service-label"
                  label="Service"
                  value={service}
                  options={serviceOptions}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  onChange={handleServiceChange}
                  disabled={readOnly || readOnlyEdit}
                  isMandatoryField={true}
                  error={errors.service}
                  errorMessage={errorMessages.service}
                  autoFocus
                />
              </Grid>
              <Grid item xs={6} md={3.8}>
                <CustomTextField
                  maxlength={4}
                  label="Work Group Code"
                  id="work-group-code"
                  value={wgcode}
                  onChange={handleWgCodeChange}
                  disabled={readOnly || readOnlyEdit}
                  isMandatoryField={true}
                  error={errors.wgcode}
                  errorMessage={errorMessages.wgcode}
                />
              </Grid>
              <Grid item xs={6} md={3.8}>
                <CustomTextField
                  maxlength={60}
                  label="Work Group Description"
                  id="work-group-description"
                  value={wgdesc}
                  onChange={handleWgDescChange}
                  disabled={readOnly}
                  isMandatoryField={true}
                  error={errors.wgdesc}
                  errorMessage={errorMessages.wgdesc}
                />
              </Grid>
            </Grid>
          </form>
          <CustomFooter
            cancelClick={handleCancel}
            saveClick={handleSubmit}
            addClick={handleAdd}
            showAddButton={true}
            readOnly={readOnly}
            buttonLabel={isEdit ? "Update" : "Save"}
            isEdit={isEdit}
          />
        </DefaultCard>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: { xs: "center", md: "end" },
          }}
        ></Box>
      </Box>
      <CustomDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onConfirm={handleDialogClose}
        title="Success"
        confirmBtn="Ok"
        content={
          isEdit ? (
            <>Data updated successfully.</>
          ) : (
            <>Data saved successfully.</>
          )
        }
      />
      <CustomDialog
        open={cancelDialogOpen}
        onClose={handleCancelDialogClose}
        content="Record(s) not Saved/Updated. Do you want to continue?"
        confirmBtn="Yes"
        cancelBtn="No"
        onConfirm={handleCancelDialogConfirm}
        onCancel={handleCancelDialogClose}
      />
      <CustomDialog
        open={unsavedAddDialogOpen}
        content="Record(s) not Saved/Updated. Do you want to continue?"
        onConfirm={handleUnsavedAddConfirm}
        onClose={handleUnsavedAddDialogClose}
        cancelBtn="No"
        confirmBtn="Yes"
      />
      <CustomDialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        onConfirm={() => setErrorDialogOpen(false)}
        title="Error"
        confirmBtn="Ok"
        content={errorMessage}
      />
    </>
  );
};

export default CreateWorkGroup;
